import { DayOfWeek, ReportScheduleRecurrence as Recurrence } from '@/__generated__/graphql';
import { type I18n } from '@lingui/core';
import { t } from '@lingui/macro';
import { Formatters } from '@/hooks/useFormatters';
import awsCronParser, { next } from 'aws-cron-parser';
import dayjs from 'dayjs';

export const getRecurrenceOptions = (i18n: I18n) => {
  return [
    { value: Recurrence.Daily, label: t(i18n)`Diariamente` },
    { value: Recurrence.Weekdays, label: t(i18n)`Los días de semana` },
    { value: Recurrence.Weekly, label: t(i18n)`Semanalmente` },
    { value: Recurrence.MonthlyFirstDay, label: t(i18n)`En la primera semana del mes` },
    { value: Recurrence.MonthlyLastDay, label: t(i18n)`En la última semana del mes` },
  ];
};

export const getRecurrenceLabel = (recurrence: Recurrence, i18n: I18n) => {
  return getRecurrenceOptions(i18n).find((option) => option.value === recurrence)?.label;
};

export const getDayOfWeekOptions = (i18n?: I18n) => {
  return [
    { enumValue: DayOfWeek.Monday, value: '2', label: i18n ? t(i18n)`Lunes` : 'aa' },
    { enumValue: DayOfWeek.Tuesday, value: '3', label: i18n ? t(i18n)`Martes` : 'aa' },
    { enumValue: DayOfWeek.Wednesday, value: '4', label: i18n ? t(i18n)`Miércoles` : 'aa' },
    { enumValue: DayOfWeek.Thursday, value: '5', label: i18n ? t(i18n)`Jueves` : 'aa' },
    { enumValue: DayOfWeek.Friday, value: '6', label: i18n ? t(i18n)`Viernes` : 'aa' },
    { enumValue: DayOfWeek.Saturday, value: '7', label: i18n ? t(i18n)`Sábado` : 'aa' },
    { enumValue: DayOfWeek.Sunday, value: '1', label: i18n ? t(i18n)`Domingo` : 'aa' },
  ].map((option) => ({
    ...option,
    lowercase: option.label[0].toLowerCase() + option.label.slice(1),
  }));
};

export const getScheduleText = (
  schedule: {
    recurrence: Recurrence;
    hours: number;
    minutes: number;
    dayOfWeek?: DayOfWeek | null;
    daysOfWeek?: DayOfWeek[] | null;
  },
  i18n: I18n,
  formatter: Formatters,
) => {
  const currentDate = new Date();
  currentDate.setHours(schedule.hours);
  currentDate.setMinutes(schedule.minutes);

  const hoursMinutesStr = formatter.formatDateTime(currentDate, {
    minute: 'numeric',
    hour: 'numeric',
  });

  const dayOfWeekOptions = getDayOfWeekOptions(i18n);
  const getDayLowercase = (day: DayOfWeek) => {
    const option = dayOfWeekOptions.find((option) => option.enumValue === day);
    if (!option) throw new Error('Error on getScheduleText');
    return option.lowercase;
  };

  const dayOfWeek = schedule.dayOfWeek ? getDayLowercase(schedule.dayOfWeek) : '';
  const daysOfWeek = schedule.daysOfWeek?.map((day) => getDayLowercase(day)) ?? [];

  const daysOfWeekStr =
    daysOfWeek.length === 1
      ? daysOfWeek[0]
      : daysOfWeek.slice(0, -1).join(', ') +
        ` ` +
        t(i18n)`y` +
        ` ` +
        daysOfWeek[daysOfWeek.length - 1];

  switch (schedule.recurrence) {
    case Recurrence.Daily:
      return t(i18n)`Diario a las ${hoursMinutesStr} horas.`;
    case Recurrence.Weekdays:
      return t(i18n)`Días de semana (lunes a viernes) a las ${hoursMinutesStr} horas.`;
    case Recurrence.Weekly:
      return t(i18n)`Semanalmente los días ${daysOfWeekStr} a las ${hoursMinutesStr} horas.`;
    case Recurrence.MonthlyFirstDay:
      return t(i18n)`Mensualmente el primer ${dayOfWeek} del mes a las ${hoursMinutesStr} horas.`;
    case Recurrence.MonthlyLastDay:
      return t(i18n)`Mensualmente el último ${dayOfWeek} del mes a las ${hoursMinutesStr} horas.`;
  }
};

export const getNextCronExecutions = (cronExpression: string, formatter: Formatters) => {
  const cron = awsCronParser.parse(cronExpression);
  const nextExecutions: string[] = [];
  let currDate = new Date();
  for (let i = 0; i < 5; i++) {
    const nextExecution = next(cron, currDate);
    if (nextExecution == null) {
      continue;
    }
    currDate = nextExecution;
    nextExecutions.push(
      formatter.formatDateTime(nextExecution, {
        timeZone: 'UTC',
        weekday: 'long',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }),
    );
  }
  return nextExecutions;
};

export const getCronExpression = (
  recurrence: Recurrence,
  schedule: {
    minutes?: number;
    hours?: number;
    dayOfWeek?: DayOfWeek;
    daysOfWeek?: DayOfWeek[];
  } = {},
): string | null => {
  const { minutes, hours } = schedule;
  const dayOfWeekOptions = getDayOfWeekOptions();
  const dayOfWeek = dayOfWeekOptions.find((option) => option.enumValue === schedule.dayOfWeek)
    ?.value;
  const daysOfWeek = schedule.daysOfWeek?.map((day) => {
    return dayOfWeekOptions.find((option) => option.enumValue === day)?.value;
  });

  switch (recurrence) {
    case Recurrence.Daily:
      if (minutes == null || hours == null) {
        return null;
      }
      return `${minutes} ${hours} * * ? *`;
    case Recurrence.Weekdays:
      if (minutes == null || hours == null) {
        return null;
      }
      return `${minutes} ${hours} ? * 2-6 *`;
    case Recurrence.Weekly:
      if (minutes == null || hours == null || daysOfWeek == null || daysOfWeek.length === 0) {
        return null;
      }
      return `${minutes} ${hours} ? * ${daysOfWeek?.join(',')} *`;
    case Recurrence.MonthlyFirstDay:
      if (minutes == null || hours == null || dayOfWeek == null) {
        return null;
      }
      return `${minutes} ${hours} ? * ${dayOfWeek}#1 *`;
    case Recurrence.MonthlyLastDay:
      if (minutes == null || hours == null || dayOfWeek == null) {
        return null;
      }
      return `${minutes} ${hours} ? * ${dayOfWeek}L *`;
  }
};

export const getHoursAndMinutes = (time: dayjs.Dayjs) => {
  return {
    hours: time.get('hours'),
    minutes: time.get('minutes'),
  };
};
